import { useCallback, useEffect, useRef } from 'react';

import * as Sentry from '@sentry/react';

// Consent can stay undecided for as long as the banner is on screen, so these
// queues need a ceiling of their own rather than trusting the client to
// appear.
const PENDING_CALLS_LIMIT = 100;

const logWarning = (message) => {
    Sentry.withScope((scope) => {
        scope.setLevel('error');
        scope.setTag('app', 'ga_events');
        Sentry.captureException(new Error(message));
    });
};

/**
 * Queues a call for a client that is not available yet, up to the limit.
 *
 * @param {{ current: Array }} queue - Pending calls for one client.
 * @param {{ current: number }} dropped - Drop counter for that client.
 * @param {'ga'|'gameGA'} client - Client name, for the report.
 * @param {{ method: string, args: unknown[] }} call - The call to queue.
 * @returns {void}
 */
const queueCall = (queue, dropped, client, call) => {
    if (queue.current.length < PENDING_CALLS_LIMIT) {
        queue.current.push(call);
        return;
    }

    dropped.current += 1;

    if (dropped.current > 1) return;

    logWarning(
        `[Deferred Analytics]: ${client} queue hit its ${PENDING_CALLS_LIMIT} call limit while the client was unavailable, further calls are dropped`
    );
};

/**
 * Replays queued calls, isolating a failure to the call that caused it.
 *
 * @param {{ current: Array }} queue - Pending calls for one client.
 * @param {object} client - The now-available client.
 * @returns {void}
 */
const flushQueue = (queue, client) => {
    const pending = queue.current;

    queue.current = [];
    pending.forEach(({ method, args }) => {
        try {
            client?.[method]?.(...args);
        } catch (error) {
            Sentry.captureException(error);
        }
    });
};

/**
 * Sends analytics immediately when a client is ready, otherwise queues until init.
 * Queued calls are flushed automatically when the corresponding client becomes available.
 *
 * `callGA` / `callGameGA` / `clearQueue` are referentially stable so they are
 * safe as effect deps.
 *
 * Calls queue while consent is undecided and are dropped while consent is
 * explicitly denied.
 *
 * @param {{ gameGA?: object, ga?: object, analyticsConsent?: boolean }} clients
 * @returns {{
 *   callGameGA: (method: string, ...args: unknown[]) => void,
 *   callGA: (method: string, ...args: unknown[]) => void,
 *   clearQueue: (client?: 'ga' | 'gameGA') => void,
 *   markClientFailed: (client: 'ga' | 'gameGA') => void,
 *   resetClientFailure: (client: 'ga' | 'gameGA') => void,
 * }}
 */
export const useDeferredAnalytics = ({ gameGA, ga, analyticsConsent }) => {
    const pendingGA = useRef([]);
    const pendingGameGA = useRef([]);
    const droppedGA = useRef(0);
    const droppedGameGA = useRef(0);
    const failedClients = useRef({ ga: false, gameGA: false });
    const gaRef = useRef(ga);
    const gameGARef = useRef(gameGA);
    const analyticsConsentRef = useRef(analyticsConsent);

    gaRef.current = ga;
    gameGARef.current = gameGA;
    analyticsConsentRef.current = analyticsConsent;

    const callGA = useCallback((method, ...args) => {
        if (analyticsConsentRef.current === false || failedClients.current.ga)
            return;

        if (gaRef.current && typeof gaRef.current[method] === 'function') {
            gaRef.current[method](...args);
        } else {
            queueCall(pendingGA, droppedGA, 'ga', { method, args });
        }
    }, []);

    const callGameGA = useCallback((method, ...args) => {
        if (
            analyticsConsentRef.current === false ||
            failedClients.current.gameGA
        )
            return;

        if (
            gameGARef.current &&
            typeof gameGARef.current[method] === 'function'
        ) {
            gameGARef.current[method](...args);
        } else {
            queueCall(pendingGameGA, droppedGameGA, 'gameGA', {
                method,
                args,
            });
        }
    }, []);

    const clearQueue = useCallback((client) => {
        if (!client || client === 'ga') {
            pendingGA.current = [];
            droppedGA.current = 0;
        }
        if (!client || client === 'gameGA') {
            pendingGameGA.current = [];
            droppedGameGA.current = 0;
        }
    }, []);

    const markClientFailed = useCallback(
        (client) => {
            failedClients.current[client] = true;
            clearQueue(client);
        },
        [clearQueue]
    );

    const resetClientFailure = useCallback((client) => {
        failedClients.current[client] = false;
    }, []);

    useEffect(() => {
        if (analyticsConsent !== false) return;

        failedClients.current = { ga: false, gameGA: false };
        clearQueue();
    }, [analyticsConsent, clearQueue]);

    useEffect(() => {
        if (
            analyticsConsent === false ||
            failedClients.current.ga ||
            !ga ||
            pendingGA.current.length === 0
        )
            return;

        flushQueue(pendingGA, ga);
    }, [ga, analyticsConsent]);

    useEffect(() => {
        if (
            analyticsConsent === false ||
            failedClients.current.gameGA ||
            !gameGA ||
            pendingGameGA.current.length === 0
        )
            return;

        flushQueue(pendingGameGA, gameGA);
    }, [gameGA, analyticsConsent]);

    return {
        callGA,
        callGameGA,
        clearQueue,
        markClientFailed,
        resetClientFailure,
    };
};
